import Axios, { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { residentialZoneKeys } from "src/query-keys";
import { FleetConfig, APIError, ResidentialZone, ResidentialZoneRestrictions } from "src/types";

export type ResidentialZoneMutationData = Pick<ResidentialZone, "name" | "polygon_frontendSrid">;
export type RestrictionsMutationData = ResidentialZoneRestrictions;
export type FleetConfigMutationData = FleetConfig;

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: ResidentialZone): ResidentialZone {
  return { ...data };
}

export function useResidentialZones(scenarioId: number | string) {
  async function queryFn(scenarioId: number | string): Promise<ResidentialZone[]> {
    const response = await Axios.get(`/planning-api/scenarios/${scenarioId}/residentialzones`);
    return response.data.map(deserialize);
  }
  return useQuery<ResidentialZone[], AxiosError<APIError[]>>(residentialZoneKeys.all(scenarioId), () =>
    queryFn(scenarioId)
  );
}

export function useResidentialZone(scenarioId: number | string, zoneId: number | string) {
  async function queryFn(scenarioId: number | string): Promise<ResidentialZone[]> {
    const response = await Axios.get(`/planning-api/scenarios/${scenarioId}/residentialzones`);
    return response.data.map(deserialize);
  }

  // TODO: can this be improved by using the `useEmissions` cache?
  return useQuery<ResidentialZone[], APIError[], ResidentialZone | undefined>(
    residentialZoneKeys.detail(scenarioId, zoneId),
    () => queryFn(scenarioId),
    {
      select: (zones) => {
        return zones.find((lez) => lez.id.toString() === zoneId.toString());
      },
    }
  );
}

export function useResidentialZoneCreateMutation(scenarioId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(scenarioId: number | string, data: ResidentialZoneMutationData) {
    const response = await Axios.post(`/planning-api/scenarios/${scenarioId}/residentialzones`, data);
    return response.data;
  }

  return useMutation<ResidentialZone, AxiosError<APIError[]>, ResidentialZoneMutationData, unknown>(
    (values: ResidentialZoneMutationData) => mutationFn(scenarioId, values),
    {
      onSettled: () => queryClient.invalidateQueries(residentialZoneKeys.all(scenarioId)),
    }
  );
}

export function useResidentialZoneUpdateMutation(scenarioId: number | string, zoneId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(zoneId: number | string, data: ResidentialZoneMutationData) {
    const response = await Axios.put(`/planning-api/residentialzones/${zoneId}`, data);
    return response.data;
  }

  return useMutation<ResidentialZone, AxiosError<APIError[]>, ResidentialZone, unknown>(
    (values: ResidentialZoneMutationData) => mutationFn(zoneId, values),
    {
      onSettled: async () => {
        await queryClient.invalidateQueries(residentialZoneKeys.all(scenarioId));
      },
    }
  );
}

export function useResidentialZoneDeleteMutation(scenarioId: number | string, zoneId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(zoneId: number | string): Promise<void> {
    return Axios.delete(`/planning-api/residentialzones/${zoneId}`);
  }

  return useMutation<void, AxiosError<APIError[]>, void, unknown>(() => mutationFn(zoneId), {
    onSettled: () => {
      queryClient.invalidateQueries(residentialZoneKeys.all(scenarioId));
    },
  });
}

export function useResidentialZoneRestrictionsUpdateMutation(scenarioId: number | string, zoneId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(zoneId: number | string, data: RestrictionsMutationData): Promise<void> {
    const response = await Axios.put(`/planning-api/residentialzones/${zoneId}/restrictions`, data);
    return response.data;
  }

  return useMutation<void, AxiosError<APIError[]>, RestrictionsMutationData, unknown>(
    (values: RestrictionsMutationData) => mutationFn(zoneId, values),
    {
      onSettled: () => queryClient.invalidateQueries(residentialZoneKeys.all(scenarioId)),
    }
  );
}
